/**
 * QrCodec — Phase 10: framing for sneakernet bundles over QR codes.
 *
 * This module owns the framing only — turning a Uint8Array bundle into a
 * sequence of self-describing frame strings, and reassembling them on the
 * receive side. The actual QR image rendering (encoding a string into a
 * QR code image) is the `qrcode` package's job; the camera-side decoding
 * is the browser's `BarcodeDetector` API.
 *
 * Frame format (single text line, ASCII):
 *
 *   ALDS-QR|v1|<bundleId>|<frameIdx>|<totalFrames>|<crc32hex>|<base64payload>
 *
 * `|` is the separator (not `/`) because base64 alphabet includes `/` —
 * splitting on `/` would slice the payload. Pipe is base64-safe.
 *
 * Where:
 *   - `bundleId` is 8 hex chars (random per-encode); lets two simultaneous
 *     sessions not get confused.
 *   - `frameIdx` is 1-based, zero-padded.
 *   - `totalFrames` matches across all frames of the same bundleId.
 *   - `crc32hex` is the CRC32 of the BASE64-DECODED frame payload, 8 hex.
 *   - `base64payload` is the frame's chunk of the bundle.
 *
 * Frame payload size defaults to 1200 bytes pre-base64 (~1600 chars
 * post-base64 + ~50 chars of framing overhead). That fits comfortably in
 * a Version-25 QR code at error-correction level M (~2.6KB capacity), so
 * the camera can read it from a normal phone distance without struggle.
 *
 * Resilience:
 *   - Each frame self-validates via CRC.
 *   - Decoder collects frames in any order (Map keyed by frameIdx).
 *   - Decoder reports missing frames explicitly so the UI can prompt to
 *     re-show that page.
 *   - bundleId mismatch on a scan = "you started a new transfer mid-scan";
 *     decoder throws.
 */

// ─────────────────────────────────────────────────────────────────────────────
// CRC32 (Ethernet polynomial, table-driven)
// ─────────────────────────────────────────────────────────────────────────────

const CRC32_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    t[i] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes: Uint8Array): number {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) {
    c = (CRC32_TABLE[(c ^ bytes[i]!) & 0xFF]! ^ (c >>> 8)) >>> 0;
  }
  return (c ^ 0xFFFFFFFF) >>> 0;
}

function crc32hex(bytes: Uint8Array): string {
  return crc32(bytes).toString(16).padStart(8, '0');
}

// ─────────────────────────────────────────────────────────────────────────────
// BASE64
// ─────────────────────────────────────────────────────────────────────────────

function toB64(bytes: Uint8Array): string {
  let s = '';
  // chunked to avoid stack overflow on very large strings
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    s += String.fromCharCode(...bytes.subarray(i, Math.min(i + chunk, bytes.length)));
  }
  return btoa(s);
}

function fromB64(s: string): Uint8Array {
  const raw = atob(s);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function randomHex(byteLen: number): string {
  const bytes = new Uint8Array(byteLen);
  crypto.getRandomValues(bytes);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += bytes[i]!.toString(16).padStart(2, '0');
  return s;
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────────────────────

export interface EncodeOptions {
  /** Pre-base64 chunk size in bytes. Default 1200 — fits Version-25 QR at error correction M. */
  chunkSize?: number;
  /** Override the random bundleId (useful for tests / determinism). */
  bundleId?: string;
}

export interface EncodeResult {
  /** Self-describing frame strings, ready to feed into a QR encoder one by one. */
  frames: string[];
  bundleId: string;
  totalFrames: number;
  /** Pre-encode byte length of the original bundle. */
  totalBytes: number;
}

export const FRAME_PREFIX = 'ALDS-QR|v1';
const SEP = '|';
const DEFAULT_CHUNK_SIZE = 1200;
const MAX_FRAMES = 9999;  // 4-digit total field

export function encodeBundle(bundle: Uint8Array, opts: EncodeOptions = {}): EncodeResult {
  const chunkSize = opts.chunkSize ?? DEFAULT_CHUNK_SIZE;
  if (chunkSize <= 0) throw new Error('QrCodec.encodeBundle: chunkSize must be positive');
  const bundleId = (opts.bundleId ?? randomHex(4)).toLowerCase();
  if (!/^[0-9a-f]{8}$/.test(bundleId)) {
    throw new Error(`QrCodec.encodeBundle: bundleId must be 8 hex chars, got ${bundleId}`);
  }

  const totalFrames = Math.max(1, Math.ceil(bundle.length / chunkSize));
  if (totalFrames > MAX_FRAMES) {
    throw new Error(`QrCodec.encodeBundle: too large — ${totalFrames} frames exceeds limit ${MAX_FRAMES}`);
  }

  const frames: string[] = [];
  for (let i = 0; i < totalFrames; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, bundle.length);
    const chunk = bundle.subarray(start, end);
    const crc = crc32hex(chunk);
    const payloadB64 = toB64(chunk);
    // 1-indexed frameIdx so users see "1 of N" not "0 of N".
    const idxStr = String(i + 1).padStart(4, '0');
    const totStr = String(totalFrames).padStart(4, '0');
    frames.push(`${FRAME_PREFIX}${SEP}${bundleId}${SEP}${idxStr}${SEP}${totStr}${SEP}${crc}${SEP}${payloadB64}`);
  }

  return { frames, bundleId, totalFrames, totalBytes: bundle.length };
}

// ─────────────────────────────────────────────────────────────────────────────
// DECODE — STATEFUL ACCUMULATOR
// ─────────────────────────────────────────────────────────────────────────────

export interface ParsedFrame {
  bundleId: string;
  frameIdx: number;   // 1-based
  totalFrames: number;
  payload: Uint8Array;
}

export class QrFrameError extends Error {
  constructor(public reason: string, public input?: string) {
    super(`QrCodec: ${reason}${input ? ` (input head: ${input.slice(0, 60)}…)` : ''}`);
    this.name = 'QrFrameError';
  }
}

export function parseFrame(s: string): ParsedFrame {
  if (!s.startsWith(`${FRAME_PREFIX}${SEP}`)) {
    throw new QrFrameError(`not an ALDS-QR|v1 frame`, s);
  }
  const parts = s.split(SEP);
  // Expected layout (7 parts): ALDS-QR, v1, bundleId, idx, total, crc, payload
  if (parts.length !== 7) {
    throw new QrFrameError(`malformed frame (expected 7 fields, got ${parts.length})`, s);
  }
  const [, , bundleId, idxStr, totStr, crcHex, payloadB64] = parts;
  if (!/^[0-9a-f]{8}$/.test(bundleId!)) throw new QrFrameError(`bad bundleId`, s);
  const frameIdx = parseInt(idxStr!, 10);
  const totalFrames = parseInt(totStr!, 10);
  if (!Number.isFinite(frameIdx) || frameIdx < 1) throw new QrFrameError(`bad frameIdx`, s);
  if (!Number.isFinite(totalFrames) || totalFrames < 1) throw new QrFrameError(`bad totalFrames`, s);
  if (frameIdx > totalFrames) throw new QrFrameError(`frameIdx > totalFrames`, s);
  if (!/^[0-9a-f]{8}$/.test(crcHex!)) throw new QrFrameError(`bad crc`, s);

  let payload: Uint8Array;
  try { payload = fromB64(payloadB64!); }
  catch { throw new QrFrameError(`bad base64`, s); }

  const actual = crc32hex(payload);
  if (actual !== crcHex) throw new QrFrameError(`crc mismatch (expected ${crcHex}, got ${actual})`, s);

  return { bundleId: bundleId!, frameIdx, totalFrames, payload };
}

export interface DecodeProgress {
  bundleId: string;
  totalFrames: number;
  received: number;
  missingFrames: number[];  // 1-based indexes still needed
  complete: boolean;
}

export class QrAccumulator {
  #bundleId: string | null = null;
  #totalFrames: number | null = null;
  #frames = new Map<number, Uint8Array>();
  /** Total bytes received (sum of payload sizes). For UI progress display. */
  #bytes = 0;

  /** Feed a raw scanned string. Returns the parsed frame (or throws via QrFrameError). */
  add(scan: string): ParsedFrame {
    const f = parseFrame(scan);
    if (this.#bundleId === null) {
      this.#bundleId = f.bundleId;
      this.#totalFrames = f.totalFrames;
    } else {
      if (f.bundleId !== this.#bundleId) {
        throw new QrFrameError(`bundleId mismatch (had ${this.#bundleId}, scanned ${f.bundleId})`);
      }
      if (f.totalFrames !== this.#totalFrames) {
        throw new QrFrameError(`totalFrames mismatch (had ${this.#totalFrames}, scanned ${f.totalFrames})`);
      }
    }
    if (!this.#frames.has(f.frameIdx)) {
      this.#frames.set(f.frameIdx, f.payload);
      this.#bytes += f.payload.length;
    }
    return f;
  }

  progress(): DecodeProgress {
    if (this.#bundleId === null || this.#totalFrames === null) {
      return { bundleId: '', totalFrames: 0, received: 0, missingFrames: [], complete: false };
    }
    const missing: number[] = [];
    for (let i = 1; i <= this.#totalFrames; i++) {
      if (!this.#frames.has(i)) missing.push(i);
    }
    return {
      bundleId: this.#bundleId,
      totalFrames: this.#totalFrames,
      received: this.#frames.size,
      missingFrames: missing,
      complete: missing.length === 0,
    };
  }

  get bundleId(): string | null { return this.#bundleId; }
  get totalFrames(): number | null { return this.#totalFrames; }
  get receivedBytes(): number { return this.#bytes; }

  /** Reassemble the bundle. Throws if any frame is missing. */
  finalize(): Uint8Array {
    const p = this.progress();
    if (!p.complete) {
      throw new QrFrameError(`incomplete — missing frames ${p.missingFrames.join(',')}`);
    }
    let total = 0;
    for (let i = 1; i <= p.totalFrames; i++) total += this.#frames.get(i)!.length;
    const out = new Uint8Array(total);
    let off = 0;
    for (let i = 1; i <= p.totalFrames; i++) {
      const chunk = this.#frames.get(i)!;
      out.set(chunk, off);
      off += chunk.length;
    }
    return out;
  }

  /** Serialize partial state to a string for localStorage persistence between scan sessions. */
  serialize(): string {
    if (this.#bundleId === null) return '';
    const obj = {
      bundleId: this.#bundleId,
      totalFrames: this.#totalFrames,
      frames: Object.fromEntries(
        [...this.#frames.entries()].map(([i, b]) => [String(i), toB64(b)]),
      ),
    };
    return JSON.stringify(obj);
  }

  /** Restore from a serialize() string. Used to resume a partial scan across page reloads. */
  static restore(s: string): QrAccumulator {
    const acc = new QrAccumulator();
    if (!s) return acc;
    const obj = JSON.parse(s) as { bundleId: string; totalFrames: number; frames: Record<string, string> };
    acc.#bundleId = obj.bundleId;
    acc.#totalFrames = obj.totalFrames;
    for (const [idxStr, b64] of Object.entries(obj.frames)) {
      const idx = parseInt(idxStr, 10);
      const bytes = fromB64(b64);
      acc.#frames.set(idx, bytes);
      acc.#bytes += bytes.length;
    }
    return acc;
  }
}

/** One-shot convenience for tests + scripts. */
export function decodeFrames(frames: string[]): Uint8Array {
  const acc = new QrAccumulator();
  for (const f of frames) acc.add(f);
  return acc.finalize();
}
